import React, { useState, useRef, useMemo, useEffect } from "react";
import Sidebar from "components/layout/sidebar/Sidebar";
import dashboardRoutes from "routes/dashboardRoutes";
import Header from "components/layout/header/Header";
import themeActions from "store/themeChanger/actions.js";
import settingactions from "store/themeSettings/actions";
import AppLocale from "i18n";
import {
  drawerWidth,
  miniDrawerWidth
} from "utils/constants";
import { Route, Routes, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { IntlProvider } from "react-intl";
import { ProtectedRoute } from "./../routes/ProtectedRoute";
import GlobalWrapper from "./global.style";

const { changeTheme } = themeActions;
const { sidebarMini, sidebarDisplay } = settingactions;

const DashboardLayout = ({ sidebarDisplay, ...props }) => {
  const [mini, setMini] = useState(
    props.themeSetting.sidebarMiniValue
      ? props.themeSetting.sidebarMiniValue === "on"
      : false
  );

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarVisible, setSidebarVisible] = useState(false); // Iniciar como fechado no mobile

  const [statedrawerWidth] = useState(drawerWidth);
  const [stateminiDrawerWidth, setStateminiDrawerWidth] = useState(
    miniDrawerWidth
  );
  const mainPanel = useRef(null);
  const scrollbars = useRef(null);
  const location = useLocation();

  useMemo(() => {
    if (scrollbars && scrollbars.current) {
      scrollbars.current.scrollToTop(0);
    }
  }, []);

  useMemo(() => {
    setMini(props.themeSetting.sidebarMiniValue === "on");
  }, [props.themeSetting.sidebarMiniValue]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarVisible(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  }, [location.pathname, isMobile]);

  let mainPanelWidth;

  const { layoutTheme, locale, themeSetting } = props;
  const currentAppLocale = AppLocale[locale];

  let routeArrowIcon = {
    backgroundColor: "#035184",
    color: "#fff",
    border: "none",
    borderBottomLeftRadius: 0,
    borderTopLeftRadius: 0,
    position: "fixed",
    zIndex: 1,
    fontSize: "0.95rem",
    padding: "4px 6px",
    minWidth: "28px",
    minHeight: "28px"
  };

  if (themeSetting.layout === "vertical") {
    mainPanelWidth = {
      width: isMobile
        ? "100%"
        : mini
        ? `calc(100% - ${miniDrawerWidth})`
        : `calc(100% - ${drawerWidth})`,
      marginLeft: isMobile ? 0 : mini ? miniDrawerWidth : drawerWidth,
      backgroundColor: layoutTheme.backgroundColor
    };
  } else if (themeSetting.layout === "horizontal") {
    mainPanelWidth = {
      width: "100%",
      backgroundColor: layoutTheme.backgroundColor
    };
  }

  const drawerMiniMethod = () => {
    if (isMobile) {
      // Apenas alterna a visibilidade no mobile
      setSidebarVisible(!sidebarVisible);
    } else {
      // Lógica para desktop
      const newMiniState = !mini;
      setMini(newMiniState);
      sidebarMini(newMiniState ? "on" : "off");
    }
  };

  const mouseEnter = () => {
    if (mini && !isMobile) {
      setStateminiDrawerWidth(drawerWidth);
    }
  };

  const mouseLeave = () => {
    if (mini && !isMobile) {
      setStateminiDrawerWidth(miniDrawerWidth);
    }
  };

  const closeDrawer = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const showSidebarHeader =
    location.pathname !== "/tour" && location.pathname !== "/gemeo-digital";

  return (
    <IntlProvider
      locale={currentAppLocale.locale}
      messages={currentAppLocale.messages}
    >
      <GlobalWrapper {...props}>
        {showSidebarHeader &&
          themeSetting.layout === "vertical" &&
          (sidebarVisible || !isMobile) && (
            <Sidebar
              mini={mini}
              drawerWidth={statedrawerWidth}
              miniDrawerWidth={stateminiDrawerWidth}
              onMouseEnter={mouseEnter}
              onMouseLeave={mouseLeave}
              closeDrawer={closeDrawer}
              {...props}
            />
          )}
        {themeSetting.layout === "horizontal" && (
          <div className="hor_mobile_sidebar">
            {showSidebarHeader && (
              <Sidebar
                mini={mini}
                drawerWidth={statedrawerWidth}
                miniDrawerWidth={stateminiDrawerWidth}
                onMouseEnter={mouseEnter}
                onMouseLeave={mouseLeave}
                closeDrawer={closeDrawer}
                {...props}
              />
            )}
          </div>
        )}

        <div
          id="main-panel"
          ref={showSidebarHeader ? mainPanel : null}
          style={{
            ...(showSidebarHeader ? mainPanelWidth : {}),
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            overflow: "hidden"
          }}
          className={showSidebarHeader ? "main-panel" : "main-panel-project"}
        >
          {/* Sidebar content moves with the header */}
          <div
            style={{
              flexShrink: 0,
              transition: "all 0.3s ease"
            }}
          >
            {showSidebarHeader &&
              themeSetting.layout === "vertical" &&
              props.themeSetting.toolbarDisplayValue === "show" && (
                <Header
                  drawerMiniMethod={drawerMiniMethod}
                  mini={mini}
                  drawerWidth={statedrawerWidth}
                  miniDrawerWidth={stateminiDrawerWidth}
                  {...props}
                />
              )}
            {themeSetting.toolbarDisplayValue !== "show" && showSidebarHeader && (
              <button
                style={routeArrowIcon}
                className="c-btn mini-drawer-menu-icon-hide-topbar"
                onClick={drawerMiniMethod}
                title={mini ? "Abrir menu" : "Recolher menu"}
              >
                <i className={mini ? "fas fa-chevron-right" : "fas fa-chevron-left"} />
              </button>
            )}
          </div>

          {/* Route Layout Start - paths relativos ao splat "/*" do pai */}
          <div
            className="overflow-auto route-height"
            style={{ flex: 1, minHeight: 0, paddingLeft: 0, paddingRight: 0 }}
          >
            <ProtectedRoute {...props}>
              <Routes>
                {dashboardRoutes.map((prop, key) => (
                  <Route
                    key={key}
                    path={prop.path.replace(/^\//, "")}
                    element={React.createElement(prop.component)}
                  />
                ))}
              </Routes>
            </ProtectedRoute>
          </div>
          {/* Route Layout Finish */}
        </div>
      </GlobalWrapper>
    </IntlProvider>
  );
};

const mapStateToProps = state => {
  return {
    ...state.themeChanger,
    LanguageSwitcher: state.LanguageSwitcher,
    locale: state.LanguageSwitcher.language.locale,
    authData: {
      token: state.auth.accessToken,
      isLogin: state.auth.isLogin
    },
    themeSetting: {
      toolbarAlignValue: state.themeSetting.toolbarAlignValue,
      footerAlignValue: state.themeSetting.footerAlignValue,
      sidebarDisplayValue: state.themeSetting.sidebarDisplayValue,
      toolbarDisplayValue: state.themeSetting.toolbarDisplayValue,
      footerDisplayValue: state.themeSetting.footerDisplayValue,
      sidebarTransParentValue: state.themeSetting.sidebarTransParentValue,
      transparentImage: state.themeSetting.transparentImage,
      activeLinkStyle: state.themeSetting.activeLinkStyle,
      sidebarMiniValue: state.themeSetting.sidebarMiniValue,
      layout: state.themeSetting.layout,
      sidebarTransParentActiveBack:
        state.themeSetting.sidebarTransParentActiveBack,
      sidebarTransParentActiveColor:
        state.themeSetting.sidebarTransParentActiveColor
    }
  };
};

export default connect(mapStateToProps, {
  changeTheme,
  sidebarMini,
  sidebarDisplay
})(DashboardLayout);
